import { schema } from 'normalizr';
import PropTypes from 'prop-types';
import Roles from './Roles';

const clientSchema = new schema.Entity('clients');

const userSchema = new schema.Entity('users', {
  client: clientSchema,
});

const accountDataSchema = new schema.Entity('accountData', {
  client: clientSchema,
});

const terminalSchema = new schema.Entity('terminals');

const cardSchema = new schema.Entity('cards');

const achSchema = new schema.Entity('achs');

const paymentDataSchema = new schema.Entity('paymentData', {
  accountData: accountDataSchema,
});

const paymentSchema = new schema.Entity('payments', {
  paymentData: paymentDataSchema,
  terminal: terminalSchema,
});

const cardShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  addressLine1: PropTypes.string.isRequired,
  addressLine2: PropTypes.string,
  alias: PropTypes.string.isRequired,
  cardNumber: PropTypes.string.isRequired,
  cardCvv: PropTypes.string.isRequired,
  cardMonth: PropTypes.string.isRequired,
  cardYear: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  postalCode: PropTypes.string.isRequired,
  stateCode: PropTypes.string.isRequired,
});

const achShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  alias: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  routingNumber: PropTypes.string.isRequired,
  contactInfo: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    countryCode: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
    postalCode: PropTypes.string,
    stateCode: PropTypes.string,
  }),
  
});

const clientShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  serialId: PropTypes.string.isRequired,
  usersLimit: PropTypes.number,
  emailDomain: PropTypes.string,
  enforceEmailDomain: PropTypes.bool,
  tpsProtocol: PropTypes.string.isRequired,
  tpsInquiryUrl: PropTypes.string,
  tpsPaymentUrl: PropTypes.string,
  apiKey: PropTypes.string,
});

const terminalShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  serialId: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  systemStatus: PropTypes.string.isRequired,
  condition: PropTypes.string.isRequired,
  healthUpdatedAt: PropTypes.dateTime,
});

const bptHealthReportShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  terminal: terminalShape,
  printerStatus: PropTypes.string,
  cashAcceptorStatus: PropTypes.string,
  emvReaderStatus: PropTypes.string,
  billScannerStatus: PropTypes.string,
  digitalIoStatus: PropTypes.string,
  allKioskAvailable: PropTypes.bool,
  kpsAvailable: PropTypes.bool,
  receivedAt: PropTypes.dateTime,
  date: PropTypes.date,
  time: PropTypes.time,
  printerPaperReport: PropTypes.shape({
    lengthForSecondPaperLowInCm: PropTypes.number,
    paperNearEndFirstReported: PropTypes.bool,
    paperNearEndSecondReported: PropTypes.bool,
    paperUsedSinceNearEndFirst: PropTypes.number,
  }),
  persistentValuesReport: PropTypes.shape({
    transactionNumber: PropTypes.number,
    failedBillScanCount: PropTypes.number,
    rejectedCashCount: PropTypes.number,
    failedCheckScanCount: PropTypes.number,
    cashAcceptorBillCount: PropTypes.number,
    audioVolume: PropTypes.number,
    transactionCount: PropTypes.number,
    transactionTotal: PropTypes.number,
    checkCount: PropTypes.number,
    checkTotal: PropTypes.number,
    creditCount: PropTypes.number,
    creditTotal: PropTypes.number,
    cash1Count: PropTypes.number,
    cash5Count: PropTypes.number,
    cash10Count: PropTypes.number,
    cash20Count: PropTypes.number,
    cash50Count: PropTypes.number,
    cash100Count: PropTypes.number,
    cashCount: PropTypes.number,
    cashTotal: PropTypes.number,
  }),
});

const userShape = PropTypes.shape({
  active: PropTypes.bool.isRequired,
  createdAt: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  lastName: PropTypes.string.isRequired,
  role: PropTypes.oneOf(Roles.values),
  client: PropTypes.number,
});

const paymentShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  paymentSerialId: PropTypes.string.isRequired,
  paymentAmount: PropTypes.number.isRequired,
  paymentType: PropTypes.string.isRequired,
  tpsPaymentReceipt: PropTypes.string.isRequired,
  processorTransactionId: PropTypes.string,
  dateTime: PropTypes.string,
  accountData: PropTypes.number,
});

const accountDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  accountNumber: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  streetDirection: PropTypes.string,
  streetSuffix: PropTypes.string,
  unitType: PropTypes.string,
  unitNumber: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
});

const paymentDataShape = PropTypes.shape({
  status: PropTypes.string.isRequired,
  statusDescription: PropTypes.string,
  tpsTransactionId: PropTypes.string,
  totalAmountDue: PropTypes.number,
  pastAmountDue: PropTypes.number,
  dueDate: PropTypes.string,
  pastDueDate: PropTypes.string,
  lastPaymentDate: PropTypes.string,
  lastPaymentAmount: PropTypes.number,
  accountData: PropTypes.number,
  validForPayment: PropTypes.bool,
});

const paginationShape = PropTypes.shape({
  currentPath: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  pageNumber: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
});

const sortingShape = PropTypes.shape({
  sortBy: PropTypes.string,
  sortDesc: PropTypes.bool.isRequired,
});

export const Shapes = {
  ACH: achShape,
  CARD: cardShape,
  CLIENT: clientShape,
  TERMINAL: terminalShape,
  BPT_HEALTH_REPORT: bptHealthReportShape,
  PAGINATION: paginationShape,
  SORTING: sortingShape,
  USER: userShape,
  PAYMENT: paymentShape,
  ACCOUNT_DATA: accountDataShape,
  PAYMENT_DATA: paymentDataShape,
};

export const Schemas = {
  CLIENT: clientSchema,
  CLIENT_ARRAY: [clientSchema],
  ACH: achSchema,
  ACH_ARRAY: [achSchema],
  CARD: cardSchema,
  CARD_ARRAY: [cardSchema],
  TERMINAL: terminalSchema,
  TERMINAL_ARRAY: [terminalSchema],
  USER: userSchema,
  USER_ARRAY: [userSchema],
  PAYMENT: paymentSchema,
  PAYMENT_ARRAY: [paymentSchema],
};
